import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTags,
  getTagList,
  gettagListWithoutPagination,
  getTagDetail,
  updateTags,
  deleteTag,
  assignDeviceTag,
  deleteDeviceTag,
  assignGatewayTag,
  deleteGatewayTag,
} from '@/services/deviceService.js';
import { QUERY_KEYS } from '@/shared/constant/queryKeys';
import { CACHE_TIMES } from '@/shared/config/reactQuery';
import useSelectedTenant from './useSelectedTenant';

const useTag = (
  tagId = null,
  page = 1,
  size = 50,
  searchFilters = null,
  options = {},
) => {
  const queryClient = useQueryClient();
  const { tenantId, hasTenant } = useSelectedTenant();
  const {
    enabled = true,
    enableList = enabled,
    enableDetail = enabled,
    allTags = false,
  } = options;

  const tagNameFilter = searchFilters?.name || null;

  // ----------------- Queries -----------------
  const {
    data: tagList,
    isLoading: isLoadingList,
    isFetching: isFetchingList,
    refetch: refetchList,
  } = useQuery({
    queryKey: [
      ...QUERY_KEYS.TAG_LIST(page, size, tagNameFilter),
      tenantId,
      allTags,
    ],
    queryFn: () =>
      allTags
        ? gettagListWithoutPagination()
        : getTagList(page, size, tagNameFilter),
    select: (response) => {
      const items = response.data?.tags || response.data || [];
      return {
        items,
        total: response.meta?.total || items.length || 0,
      };
    },
    enabled: enableList && hasTenant,
    staleTime: CACHE_TIMES.SHORT,
    gcTime: CACHE_TIMES.MEDIUM,
  });

  const { data: tagDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: [...QUERY_KEYS.TAG_DETAIL(tagId), tenantId],
    queryFn: () => getTagDetail(tagId),
    enabled: !!tagId && enableDetail && hasTenant,
    select: (response) => response.data,
  });

  // ----------------- Base CRUD Mutations -----------------
  const { mutateAsync: createTag, isPending: isCreating } = useMutation({
    mutationFn: ({ name }) => createTags(name),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TAG_ALL });
    },
  });

  const { mutateAsync: updateTag, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, name }) => updateTags(id, name),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TAG_ALL });
    },
  });

  const { mutateAsync: deleteTagItem, isPending: isDeleting } = useMutation({
    mutationFn: (id) => deleteTag(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TAG_ALL });
    },
  });

  // ----------------- Gateway Mutations -----------------
  const { mutateAsync: assignGateway, isPending: isAssigningGateway } =
    useMutation({
      mutationFn: ({ id, gatewayIds }) => assignGatewayTag(id, gatewayIds),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TAG_ALL }),
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.GATEWAY_ALL }),
        ]);
      },
    });

  const { mutateAsync: removeGatewayFromTag } = useMutation({
    mutationFn: ({ id, gatewayTagId }) => deleteGatewayTag(gatewayTagId, id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TAG_ALL }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.GATEWAY_ALL }),
      ]);
    },
  });

  // ----------------- Device Mutations -----------------
  const { mutateAsync: assignDevice, isPending: isAssigningDevice } =
    useMutation({
      mutationFn: ({ id, deviceIds }) => assignDeviceTag(id, deviceIds),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TAG_ALL }),
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DEVICE_ALL }),
        ]);
      },
    });

  const { mutateAsync: removeDeviceFromTag } = useMutation({
    mutationFn: ({ id, deviceTagId }) => deleteDeviceTag(deviceTagId, id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TAG_ALL }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DEVICE_ALL }),
      ]);
    },
  });

  return {
    tagList,
    isLoadingList,
    isFetchingList,
    refetchList,
    tagDetail,
    isLoadingDetail,

    // Base CRUD
    createTag,
    isCreating,
    updateTag,
    isUpdating,
    deleteTag: deleteTagItem,
    isDeleting,

    // Gateway Operations
    assignGateway,
    isAssigningGateway,
    removeGatewayFromTag,

    // Device Operations
    assignDevice,
    isAssigningDevice,
    removeDeviceFromTag,
  };
};

export default useTag;
